// src/modules/app-views/crm/pages/Document/modals/RescheduleModal/index.jsx
// *******************************************************************************
// RESCHEDULE MODAL
// *******************************************************************************
// Simple GenericModal (1 step) that reschedules a CRM opportunity:
//   1. Creates an agenda calendar event
//   2. Updates attendance.rescheduledTo on the CRM document
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import crmService from 'modules/app-views/crm/service';
import agendasService from 'modules/app-views/crm/agendasService';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import Step1 from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RescheduleModal = ({ show, setShow, docId, opportunityName, onSuccess }) => {
  // ---------------------------------------------------------------------------
  // GLOBAL STATE
  // ---------------------------------------------------------------------------
  const storedUser = useZustand('auth', 'user');

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const steps = [
    {
      stepTitle: translateX('reschedule_opportunity'),
      content: () => <Step1 />,
      validationFields: ['date', 'time'],
    },
  ];

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ form, setLoading }) => {
    const { date, time, note } = form.getFieldsValue(true);

    setLoading(true);
    try {
      const rescheduledTo = dayjs(date)
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .toISOString();

      // Build deep link so the attendant can navigate to this CRM document from the agenda
      const documentUrl = `${window.location.origin}${window.location.pathname}`;

      // Create agenda calendar event
      await agendasService.save({
        title: `${translateX('reschedule_opportunity')}: ${opportunityName || ''}`,
        date: dayjs(date).format('YYYY-MM-DD'),
        startTime: time.format('HH:mm'),
        eventType: 'meeting',
        notes: note || '',
        linkedDocumentId: docId || null,
        linkedDocumentUrl: documentUrl,
      });

      // Build the timeline entry text: "Contato reagendado para DD/MM/YYYY HH:mm [+ note]"
      const formattedDate = dayjs(rescheduledTo).format('DD/MM/YYYY HH:mm');
      const followUpText = note
        ? `${translateX('rescheduled_to')} ${formattedDate}\n${note}`
        : `${translateX('rescheduled_to')} ${formattedDate}`;

      // Update CRM opportunity with rescheduled date + create follow-up timeline entry
      await crmService.rescheduleOpportunity(docId, {
        rescheduledTo,
        note: note || '',
        followUpText,
        accountId: storedUser?._id,
        accountName: storedUser?.name || null,
        accountAvatarUrl:
          storedUser?.avatar?.base64Thumb ||
          storedUser?.avatar?.thumbUrl ||
          null,
      });

      appService.message({ flag: 's', text: 'rescheduled_success' });
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message;
      appService.message({ flag: 'e', text: msg || 'error_saving_document' });
      setLoading(false);
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={translateX('reschedule_opportunity')}
      icon={<CalendarOutlined />}
      steps={steps}
      onFinish={handleFinish}
      width={400}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RescheduleModal;
